import {
  useHttpClient,
  useNetworkStatus,
} from '@airship-platform/airship-patterns';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useReducer,
} from 'react';
import { useAppointmentProposalFiles } from '../appointmentProposalFiles';
import { type JobFileImage } from './jobImages.models';
import { initialState, jobImagesReducer } from './jobImages.reducer';

interface JobImagesContextProps {
  availableImages: JobFileImage[];
  isLoading: boolean;
  isLoaded: boolean;
  isAttaching: boolean;
  fetchJobImages: () => void;
  attachJobImages: (
    appointmentId: string,
    appointmentProposalId: string,
    selectedFileIds: string[]
  ) => Promise<void>;
}

const JobImagesContext = createContext<JobImagesContextProps | undefined>(
  undefined
);

export const JobImagesProvider: React.FC<
  PropsWithChildren<{ jobId: string }>
> = ({ children, jobId }) => {
  const [state, dispatch] = useReducer(jobImagesReducer, initialState);
  const isOnline = useNetworkStatus();
  const httpClient = useHttpClient();
  const { files, fetchAppointmentFiles } = useAppointmentProposalFiles();

  const jobImages = useMemo(
    () =>
      state.ids
        .map((id) => state.entities[id])
        .filter((img) => img.jobId === state.currentJobId),
    [state.ids, state.entities, state.currentJobId]
  );

  const attachedFilenames = useMemo(
    () =>
      new Set(
        files
          .filter((f) => f.metadata.uploadCategory === 'JOB_ATTACHMENT')
          .map((f) => f.filename)
      ),
    [files]
  );

  const availableImages = useMemo(
    () =>
      jobImages.filter((img) => {
        const filename = img.fileName || img.title || 'job-attachment';
        return !attachedFilenames.has(filename);
      }),
    [jobImages, attachedFilenames]
  );

  const fetchJobImages: JobImagesContextProps['fetchJobImages'] =
    useCallback(async () => {
      if (!isOnline()) {
        dispatch({
          type: 'SET_LOADING_STATE',
          payload: { isLoading: false, isLoaded: true },
        });
        return;
      }

      dispatch({ type: 'FETCH_JOB_IMAGES', payload: { jobId } });
      try {
        const allFiles = await httpClient.get<JobFileImage[]>(
          `/jobs/${jobId}/files`
        );
        const images = allFiles.filter((f) =>
          f.contentType?.startsWith('image/')
        );
        dispatch({
          type: 'FETCH_JOB_IMAGES_SUCCESS',
          payload: { jobId, images },
        });
      } catch {
        dispatch({
          type: 'FETCH_JOB_IMAGES_FAILURE',
          payload: { error: 'Unable to load job photos' },
        });
      }
    }, [httpClient, jobId, isOnline]);

  const attachJobImages: JobImagesContextProps['attachJobImages'] = useCallback(
    async (appointmentId, appointmentProposalId, selectedFileIds) => {
      dispatch({ type: 'ATTACH_JOB_IMAGES' });
      try {
        await httpClient.post(
          `/appointments/${appointmentId}/proposals/${appointmentProposalId}/job-media`,
          { selectedFiles: selectedFileIds }
        );
        dispatch({ type: 'ATTACH_JOB_IMAGES_SUCCESS' });
        fetchAppointmentFiles();
      } catch {
        dispatch({
          type: 'ATTACH_JOB_IMAGES_FAILURE',
          payload: { error: 'Failed to attach job photos' },
        });
      }
    },
    [httpClient, fetchAppointmentFiles]
  );

  return (
    <JobImagesContext.Provider
      value={{
        availableImages,
        isLoading: state.isLoading,
        isLoaded: state.isLoaded,
        isAttaching: state.isAttaching,
        fetchJobImages,
        attachJobImages,
      }}
    >
      {children}
    </JobImagesContext.Provider>
  );
};

export const useJobImagesProvider = () => {
  const context = useContext(JobImagesContext);
  if (!context) {
    throw new Error(
      'useJobImagesProvider must be used within a JobImagesProvider'
    );
  }
  return context;
};
